import { CartAddEvent } from '@theme/events';
import '@theme/product-form';

/**
 * <bundle-builder>, build-your-own-bundle widget.
 *
 * Keeps a running total as slot choices change, and on add posts the chosen
 * component variants to /cart/add.js, all sharing one `_bundle_id` so the cart
 * can group them and add/remove them together.
 *
 * Slots are optional past the first: the more the shopper fills, the higher the
 * tier of the ladder they reach. The discount itself is applied by a Shopify
 * Functions discount, not here - the ladder in the config is display only, so if
 * the two ever disagree the cart is right and this widget is wrong.
 */
class BundleBuilder extends HTMLElement {
  connectedCallback() {
    // [{slots, percent}], how many filled slots earn what discount.
    try {
      this.tiers = JSON.parse(this.dataset.tiers || '[]');
    } catch {
      this.tiers = [];
    }
    // Names which bundle this is so the discount can look its price up. The
    // price itself is never sent, a line property is shopper-editable.
    this.bundleKey = this.dataset.bundleKey || '';
    this.chooseHint = this.dataset.chooseHint || 'Choose one';
    this.bundleTitle = this.dataset.bundleTitle || this.dataset.productTitle || 'Bundle';
    this.currency = this.dataset.currency || 'GBP';

    this.slots = [...this.querySelectorAll('[data-bb-slot]')];
    this.addButton = this.querySelector('[data-bb-add]');
    this.tierEl = this.querySelector('[data-bb-tier]');
    this.meterEl = this.querySelector('[data-bb-meter]');
    this.trackEl = this.querySelector('[data-bb-track]');
    this.stopEls = [...this.querySelectorAll('[data-bb-stop]')];
    // The furthest rung sets the scale, so the bar fills exactly as the last
    // tier is reached rather than running off the end.
    this.maxSlots = this.stopEls.reduce(
      (max, el) => Math.max(max, Number(el.dataset.slots) || 0),
      0
    );
    // The bar spans first rung to last, so progress is measured from the lowest
    // threshold rather than from zero products.
    this.minSlots = Number(this.trackEl?.dataset.minSlots) || 1;
    this.tierMessage = this.dataset.tierMessage || 'Add [n] more for [percent]% off';
    this.tierEarned = this.dataset.tierEarned || '[percent]% bundle discount applied';
    this.totalEl = this.querySelector('[data-bb-total]');
    this.wasEl = this.querySelector('[data-bb-was]');

    this.priceNodes = [
      ...(this.closest('.shopify-section')?.querySelectorAll('product-price .price') ?? []),
    ];
    this.originalPrices = this.priceNodes.map((node) => node.textContent);

    // The theme only renders a compare-at when the product itself is on sale, so
    // one is created per price node to carry the pre-discount subtotal.
    this.compareNodes = this.priceNodes.map((node) => {
      const existing = node.parentElement?.querySelector('.compare-at-price');
      if (existing) return existing;

      // Mirror the theme's own markup from snippets/price.liquid: each price
      // sits in an inline-block .price-item__group, and the gap between them
      // comes from the &nbsp; in the visually-hidden label rather than CSS.
      const group = document.createElement('span');
      group.className = 'price-item__group';
      group.hidden = true;

      const label = document.createElement('span');
      label.className = 'visually-hidden';
      label.innerHTML = 'Regular price&nbsp;';

      const el = document.createElement('s');
      el.className = 'price-item--regular compare-at-price';

      group.append(label, el);

      // The theme's own gap is the newline between the two inline-block groups
      // in price.liquid, which collapses to a single space. The visually-hidden
      // label has no layout width, so its &nbsp; contributes nothing, and an
      // element inserted by script has no whitespace around it at all.
      const anchor = node.closest('.price-item__group') ?? node;
      anchor.after(group);
      group.before(document.createTextNode(' '));

      // The hidden flag lives on the group so the label goes with it.
      el.group = group;
      return el;
    });

    this.money = new Intl.NumberFormat(undefined, { style: 'currency', currency: this.currency });

    this.onChange = this.onChange.bind(this);
    this.onAdd = this.onAdd.bind(this);

    this.onToggle = this.onToggle.bind(this);
    this.onClear = this.onClear.bind(this);
    this.onQuantity = this.onQuantity.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onToggle);
    this.addEventListener('click', this.onClear);
    this.addEventListener('click', this.onQuantity);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
    this.addButton?.addEventListener('click', this.onAdd);

    this.#suppressStandardBuyButtons();
    this.#restore();
    this.recalculate();
    this.#refreshStock();
  }

  /** Where this bundle's in-progress build is kept for the rest of the session. */
  get #storageKey() {
    return `bb:${this.bundleKey || this.dataset.productId || 'bundle'}`;
  }

  /**
   * Remembers the build for the session, so wandering off to read a product page
   * and coming back doesn't mean picking fourteen things again.
   *
   * Session rather than local: a half-built bundle is a train of thought, not a
   * setting, and finding last week's selection pre-ticked would be a surprise.
   * Storage can throw outright in a private window, so every access is guarded
   * and failure just means the feature quietly isn't there.
   */
  #persist() {
    const state = this.selection.map((choice) => [choice.variantId, choice.quantity]);
    try {
      if (state.length) {
        sessionStorage.setItem(this.#storageKey, JSON.stringify(state));
      } else {
        sessionStorage.removeItem(this.#storageKey);
      }
    } catch {
      /* private window, or storage disabled */
    }
  }

  #restore() {
    let state;
    try {
      state = JSON.parse(sessionStorage.getItem(this.#storageKey) || '[]');
    } catch {
      return;
    }
    if (!Array.isArray(state) || state.length === 0) return;

    for (const entry of state) {
      const [variantId, quantity] = Array.isArray(entry) ? entry : [];
      // A row can be gone since it was stored: sold out, or removed from the
      // bundle entirely. Skipping it beats restoring something unbuyable.
      const input = this.querySelector(`[data-bb-choice][value="${CSS.escape(String(variantId))}"]`);
      if (!input || input.disabled) continue;

      input.checked = true;
      input.dataset.qty = String(Math.max(1, Number(quantity) || 1));
    }

    this.#refreshQuantities();
    this.#refreshClears();
    this.#refreshToggles();
  }

  /**
   * Price and stock are rendered from a snapshot the setup screen wrote into the
   * bundle config, because resolving 80-odd products in Liquid is not possible:
   * a product reference list hydrates only its first 50 entries and all_products
   * covers just 20 more per page. The snapshot goes stale the moment inventory
   * moves, so the live figures are fetched here and the rows corrected. A failed
   * or slow request leaves the snapshot in place, which is the same position the
   * page was in before.
   */
  async #refreshStock() {
    const token = this.dataset.storefrontToken;
    if (!token) return;

    const rows = [...this.querySelectorAll('.bb__choice')];
    const ids = [
      ...new Set(
        rows.map((row) => row.querySelector('[data-bb-choice]')?.value).filter(Boolean)
      ),
    ];
    if (!ids.length) return;

    // Without a country the API answers for no market in particular, and a
    // variant stocked only in another country comes back available right up
    // until the cart refuses it. @inContext pins the question to the market the
    // shopper is actually buying in.
    const country = this.dataset.country;
    const context = country ? `@inContext(country: ${country})` : '';
    const query = `query Stock($ids: [ID!]!) ${context} {
      nodes(ids: $ids) {
        ... on ProductVariant {
          id
          availableForSale
          price { amount }
        }
      }
    }`;

    let nodes;
    try {
      // Same-origin, and the Storefront token is public by design.
      const res = await fetch(`/api/${this.dataset.apiVersion || '2024-10'}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({
          query,
          variables: { ids: ids.map((id) => `gid://shopify/ProductVariant/${id}`) },
        }),
      });
      nodes = (await res.json())?.data?.nodes;
    } catch {
      return;
    }
    if (!Array.isArray(nodes)) return;

    const live = new Map();
    for (const node of nodes) {
      if (!node?.id) continue;
      live.set(String(node.id).split('/').pop(), node);
    }

    let changed = false;
    for (const row of rows) {
      const input = row.querySelector('[data-bb-choice]');
      if (!input) continue;

      const node = live.get(String(input.value));
      // A variant the Storefront API won't return has been deleted or
      // unpublished, which is as good as sold out for a bundle line.
      const available = node ? node.availableForSale !== false : false;

      if (!available) {
        if (input.checked) {
          input.checked = false;
          changed = true;
        }
        // Faded and unpickable rather than removed, matching what the server
        // renders for anything already sold out at save time.
        input.disabled = true;
        row.classList.add('bb__choice--gone');
        continue;
      }

      // Back in stock since the snapshot was written, so undo the fade.
      if (row.classList.contains('bb__choice--gone')) {
        input.disabled = false;
        row.classList.remove('bb__choice--gone');
        changed = true;
      }

      const pence = node.price?.amount == null ? null : Math.round(Number(node.price.amount) * 100);
      if (pence != null && String(pence) !== input.dataset.price) {
        input.dataset.price = String(pence);
        const priceEl = row.querySelector('.bb__price');
        if (priceEl) priceEl.textContent = `Worth ${this.money.format(pence / 100)}`;
        changed = true;
      }
    }

    // A slot with nothing choosable left can't be filled, so it stops claiming a
    // place in the ladder. Sold out rows still count as content, so the test is
    // what remains pickable rather than what remains visible.
    for (const slot of this.slots) {
      const usable = slot.querySelectorAll('.bb__choice:not(.bb__choice--gone)').length;
      slot.hidden = usable === 0;
    }

    if (changed || rows.some((row) => row.classList.contains('bb__choice--gone'))) {
      this.recalculate();
    }
  }

  /**
   * The stylesheet hides the standard buy buttons; this stops them working.
   * CSS alone would leave a submittable form behind, reachable by keyboard,
   * by a script, or by anything that unhides it - and submitting it sells the
   * bundle product on its own at its display price with no components.
   */
  #suppressStandardBuyButtons() {
    const section = this.closest('.shopify-section') ?? document;
    for (const form of section.querySelectorAll('form[action*="/cart/add"]')) {
      if (form.closest('bundle-builder')) continue;
      form.setAttribute('inert', '');
      for (const control of form.querySelectorAll('button, input, select')) {
        control.disabled = true;
      }
    }
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('click', this.onToggle);
    this.removeEventListener('click', this.onClear);
    this.removeEventListener('click', this.onQuantity);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    this.addButton?.removeEventListener('click', this.onAdd);
  }

  onChange(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!event.target.hasAttribute('data-bb-choice')) return;

    // A one-option slot is fixed content rendered as a checked radio. Nothing
    // can deselect it, but guard anyway so a stray click can't drop it.
    if (event.target.hasAttribute('data-bb-locked')) event.target.checked = true;

    const slot = event.target.closest('[data-bb-slot]');
    const choices = slot?.querySelector('[data-bb-choices]');
    const toggle = slot?.querySelector('[data-bb-toggle]');

    // A single-choice slot is done as soon as something is picked. A multi slot
    // stays open, since the shopper is likely to take more than one.
    if (choices && toggle && !slot.hasAttribute('data-bb-multiple')) {
      choices.setAttribute('hidden', '');
      toggle.setAttribute('aria-expanded', 'false');
    }

    this.recalculate();
  }

  /**
   * Steps a choice's quantity. Quantity changes the price but never the tier:
   * the ladder counts distinct products, so two of one item is still one step.
   * @param {Event} event
   */
  onQuantity(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-bb-qty-step]') : null;
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    const row = button.closest('.bb__choice');
    const input = row?.querySelector('[data-bb-choice]');
    if (!input) return;

    const step = Number(button.dataset.bbQtyStep) || 0;
    const next = Math.max(1, (parseInt(input.dataset.qty || '1', 10) || 1) + step);
    input.dataset.qty = String(next);

    const value = row.querySelector('[data-bb-qty-value]');
    if (value) value.textContent = String(next);

    this.recalculate();
  }

  /** Shows a stepper only on rows that are actually in the bundle. */
  #refreshQuantities() {
    for (const row of this.querySelectorAll('.bb__choice')) {
      const stepper = row.querySelector('[data-bb-qty]');
      if (!stepper) continue;

      const input = row.querySelector('[data-bb-choice]');
      const checked = input?.checked === true;
      stepper.toggleAttribute('hidden', !checked);

      // A product removed from the bundle shouldn't remember its old quantity.
      if (!checked && input) {
        input.dataset.qty = '1';
        const value = row.querySelector('[data-bb-qty-value]');
        if (value) value.textContent = '1';
      }
    }
  }

  /** Closes every open slot. */
  #closeAll(except) {
    for (const slot of this.slots) {
      if (slot === except) continue;
      const choices = slot.querySelector('[data-bb-choices]');
      const toggle = slot.querySelector('[data-bb-toggle]');
      if (!choices || !toggle) continue;
      choices.setAttribute('hidden', '');
      toggle.setAttribute('aria-expanded', 'false');
    }
  }

  /** A floating list has to close when the shopper looks away from it. */
  onDocumentClick(event) {
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#closeAll();
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;
    this.#closeAll();
  }

  /**
   * Empties a slot. Slots are optional, and clicking a checked radio can't
   * uncheck it, so this is the only route back to "none of these".
   * @param {Event} event
   */
  onClear(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-bb-clear]') : null;
    if (!button) return;

    const slot = button.closest('[data-bb-slot]');
    for (const input of slot?.querySelectorAll('[data-bb-choice]') ?? []) {
      if (!input.hasAttribute('data-bb-locked')) input.checked = false;
    }
    this.recalculate();
  }

  /**
   * Expands or collapses a slot. Long slots start collapsed so a nine-option
   * list doesn't bury everything below it.
   * @param {Event} event
   */
  onToggle(event) {
    const toggle = event.target instanceof Element ? event.target.closest('[data-bb-toggle]') : null;
    if (!toggle) return;

    const slot = toggle.closest('[data-bb-slot]');
    const choices = slot?.querySelector('[data-bb-choices]');
    if (!choices) return;

    const open = choices.hasAttribute('hidden');
    // Two floating panels would overlap each other.
    this.#closeAll(slot);
    choices.toggleAttribute('hidden', !open);
    toggle.setAttribute('aria-expanded', String(open));
  }

  /** The percentage earned by filling `count` slots, highest tier reached. */
  /**
   * What the discount function will actually take off, in pence.
   *
   * Not `subtotal * percent`. The discount is a percentage taken off each unit,
   * floored to the penny, and only then multiplied by the quantity. Both of the
   * shortcuts are wrong in their own direction:
   *
   *   round the whole basket   15% of £10.99 comes off as £1.65, cart says £1.64
   *   floor the whole line     £11.99 x4 at 20% loses 959p, cart takes 956p
   *
   * Flooring per unit matches both: a single £10.99 line loses 164p either way,
   * and a line of four loses 4 x 239p rather than one floor of 959.2p.
   */
  #discountFor(selection, percent) {
    if (!percent) return 0;

    return selection.reduce(
      // Multiplied before dividing, so the pence stay integers as long as
      // possible and 1099 * 15 is 16485 rather than a float ending in 84999.
      (sum, choice) =>
        sum + Math.floor((choice.price * percent) / 100) * choice.quantity,
      0,
    );
  }

  #percentageFor(count) {
    let best = 0;
    for (const tier of this.tiers) {
      const slots = Number(tier?.slots);
      const percent = Number(tier?.percent);
      if (!Number.isFinite(slots) || !Number.isFinite(percent)) continue;
      if (count >= slots && percent > best) best = percent;
    }
    return best;
  }

  /** The next rung of the ladder that's still out of reach. */
  #nextTier(count) {
    return this.tiers
      .filter((tier) => Number(tier?.slots) > count && Number(tier?.percent) > 0)
      .sort((a, b) => Number(a.slots) - Number(b.slots))[0];
  }

  /** Tells the shopper what they've earned and what one more slot would earn. */
  #refreshTier(count, percent) {
    const next = this.#nextTier(count);

    if (this.tierEl) {
      const parts = [];
      if (percent > 0) parts.push(this.tierEarned.replaceAll('[percent]', String(percent)));
      if (next) {
        parts.push(
          this.tierMessage
            .replaceAll('[n]', String(Number(next.slots) - count))
            .replaceAll('[percent]', String(Number(next.percent)))
        );
      }
      this.tierEl.textContent = parts.join('. ');
    }

    const span = this.maxSlots - this.minSlots;
    if (!this.meterEl || span < 1) return;

    const progress = Math.min(100, Math.max(0, ((count - this.minSlots) / span) * 100));
    this.meterEl.style.setProperty('--bb-progress', `${progress}%`);
    this.trackEl?.setAttribute('aria-valuenow', String(Math.round(progress)));

    for (const stop of this.stopEls) {
      stop.toggleAttribute('data-reached', count >= (Number(stop.dataset.slots) || 0));
    }
  }

  /** A Clear button only makes sense on a slot that has something to clear. */
  #refreshClears() {
    for (const slot of this.slots) {
      const button = slot.querySelector('[data-bb-clear]');
      if (!button) continue;
      const checked = slot.querySelector('[data-bb-choice]:checked:not([data-bb-locked])');
      button.toggleAttribute('hidden', !checked);
    }
  }

  /** Mirrors each collapsed slot's selection into its toggle. */
  #refreshToggles() {
    for (const slot of this.slots) {
      const label = slot.querySelector('[data-bb-toggle-label]');
      if (!label) continue;

      const all = [...slot.querySelectorAll('[data-bb-choice]:checked')];

      // Listing four product names in a collapsed row is unreadable, so a multi
      // slot summarises by count once there's more than one.
      if (slot.hasAttribute('data-bb-multiple') && all.length > 1) {
        label.textContent = `${all.length} selected`;
        continue;
      }

      const checked = all[0];

      if (!checked) {
        label.textContent = this.chooseHint;
        continue;
      }
      const row = checked.nextElementSibling;
      const title = row?.querySelector('.bb__title')?.textContent.trim() ?? '';
      const variant = row?.querySelector('.bb__variant')?.textContent.trim() ?? '';
      const price = row?.querySelector('.bb__price')?.textContent.trim() ?? '';
      // Same reason the card carries it: two variants of one product collapse to
      // the same summary otherwise.
      const name = variant ? `${title} - ${variant}` : title;
      label.textContent = price ? `${name} - ${price}` : name;
    }
  }

  /**
   * Every chosen product, in slot order. A multi slot can contribute several,
   * each with its own quantity; a single slot contributes at most one.
   */
  get selection() {
    return this.slots.flatMap((slot) => {
      // Carried through to the cart so a required slot's product can't be
      // stepped down to zero and removed on its own.
      const required = slot.hasAttribute('data-bb-required');

      return [...slot.querySelectorAll('[data-bb-choice]:checked')].map((input) => ({
        variantId: Number(input.value),
        price: parseInt(input.dataset.price || '0', 10),
        quantity: Math.max(1, parseInt(input.dataset.qty || '1', 10)),
        required,
      }));
    });
  }

  recalculate() {
    const selection = this.selection;
    const subtotal = selection.reduce((sum, choice) => sum + choice.price * choice.quantity, 0);

    // One filled slot per selected line, which is what the ladder counts.
    const percent = this.#percentageFor(selection.length);
    const total = subtotal - this.#discountFor(selection, percent);

    if (this.totalEl) this.totalEl.textContent = this.money.format(total / 100);
    if (this.wasEl) this.wasEl.textContent = this.money.format(subtotal / 100);

    // Keep the page price in step with the build. With nothing chosen there is
    // no bundle total yet, so the product's own price goes back rather than the
    // £0.00 an empty selection would otherwise produce.
    this.priceNodes.forEach((node, index) => {
      // Nothing chosen means there is no bundle price yet, and the product's own
      // price is meaningless here, so the whole figure is hidden rather than
      // showing a number the shopper would have to discount mentally.
      const target = node.closest('.price-item__group') ?? node;
      target.hidden = selection.length === 0;

      node.textContent =
        selection.length === 0 ? this.originalPrices[index] : this.money.format(total / 100);

      // Compare-at is the undiscounted subtotal, shown only when a tier has
      // actually taken something off, otherwise it would just repeat the price.
      const compare = this.compareNodes[index];
      if (!compare) return;

      const worthShowing = selection.length > 0 && subtotal > total;
      compare.textContent = worthShowing ? this.money.format(subtotal / 100) : '';
      (compare.group ?? compare).hidden = !worthShowing;
    });

    // One slot is enough to buy, the rest only sweeten the price - unless a slot
    // is marked required, in which case it has to be filled first.
    const missingRequired = this.slots.some(
      (slot) =>
        slot.hasAttribute('data-bb-required') &&
        !slot.querySelector('[data-bb-choice]:checked')
    );
    if (this.addButton) this.addButton.disabled = selection.length < 1 || missingRequired;

    this.#refreshTier(selection.length, percent);
    this.#refreshClears();
    this.#refreshQuantities();

    this.#refreshToggles();

    this.total = total;
    this.subtotal = subtotal;
    this.selected = selection;

    this.#persist();
  }

  async onAdd() {
    if (!this.selected?.length) return;

    // Unique per add, so two bundles in one cart stay separate groups rather
    // than merging into one oversized line.
    const bundleId = `${this.dataset.productId}-${Date.now().toString(36)}${Math.random()
      .toString(36)
      .slice(2, 8)}`;

    // Only the chosen components go in the cart, this product is the page, not
    // a line. `_bundle_title` rides along so the cart can label the group
    // without looking anything up.
    const items = this.selected.map((choice) => ({
      id: choice.variantId,
      quantity: choice.quantity,
      properties: {
        _bundle_id: bundleId,
        _bundle_key: this.bundleKey,
        _bundle_title: this.bundleTitle,
        ...(choice.required && { _bundle_required: 'true' }),
      },
    }));

    this.addButton.setAttribute('aria-busy', 'true');

    // Fired on click, not after the request, which is what the theme's own
    // buy buttons do. Waiting for /cart/add.js meant the drawer had already
    // opened over the button by the time the burst played, so nobody saw it.
    this.querySelector('add-to-cart-component')?.animateAddToCart?.();

    try {
      const root = window.Shopify?.routes?.root || '/';

      const addRes = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!addRes.ok) throw new Error(`cart/add failed: ${addRes.status}`);

      const cartRes = await fetch(`${root}cart.js`);
      const cart = await cartRes.json();

      this.#trackAddToCart(cart);

      document.dispatchEvent(
        new CartAddEvent(cart, this.id || 'bundle-builder', {
          source: 'bundle-builder',
          itemCount: cart.item_count,
        })
      );
    } catch (error) {
      console.error('[bundle-builder]', error);
    } finally {
      this.addButton.removeAttribute('aria-busy');
    }
  }

  /**
   * GA4 `add_to_cart` for the bundle as one product, valued at the bundle
   * price rather than the sum of its components, the components aren't
   * separately purchasable here, so counting them would double-count revenue.
   * @param {any} cart - The cart.js response.
   */
  #trackAddToCart(cart) {
    const track = /** @type {any} */ (window).track;
    if (typeof track !== 'function' || !Array.isArray(cart?.items)) return;

    track('add_to_cart', {
      currency: this.currency,
      value: (this.total || 0) / 100,
      item_list_name: 'bundle_builder',
      items: [
        {
          item_id: String(this.dataset.productId),
          item_name: this.bundleTitle,
          price: (this.total || 0) / 100,
          quantity: 1,
          index: 0,
          item_list_name: 'bundle_builder',
        },
      ],
    });
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
